import { firstBy } from 'thenby';
import moment from 'moment-timezone';
import { call, put, select } from 'redux-saga/effects';
import api from '../../../services/api';
import actions, { setCrewSortOption } from './actions';
import LocalCache from '../../../pages/local-storage/local-storage';
import { getUserRole } from '../../selectors';
import { ApiTypes } from '../../../models';
import { orderBy } from 'lodash';
import { CompanySettings } from '../../../common/types/Company';
import Profile = ApiTypes.Profile;

type Pilot = ApiTypes.Pilot;

export function* loadPilotsWorker() {
  const companySettings = yield select(({ user: { Company } }) => Company.Settings);
  const companyID = yield select(({ user: { Company } }) => Company.ID);
  const user = yield select(({ user }) => user);
  const { selectedTags, entryPeriod } = yield select(
    ({ pages: { crewSchedulingV2 } }) => crewSchedulingV2,
  );
  const userRoles = yield select(getUserRole) || [];
  const localCache = new LocalCache('crewSchedulingV2', user);

  const queryParams = { is_pilot: true, limit: -1, order: 'profile.last_name asc' };
  if (selectedTags.length > 0) {
    queryParams['crew_schedule_tags'] = selectedTags;
    queryParams['user_tags'] = selectedTags;

    // TODO: API doesn't support this parameters yet
    // const [startTime, endTime] = entryPeriod;
    // if (startTime && endTime) {
    //   queryParams['crew_schedule_tags_date_start'] = startTime;
    //   queryParams['crew_schedule_tags_date_end'] = endTime;
    // }
  }

  const {
    data: { Data },
  } = yield call(api.get, `/v1/companies/${companyID}/users`, {
    params: queryParams,
  });

  if (!Data) {
    return {
      sortedPilots: [],
      selectedPilotsData: [],
      orderedByFirstNamePilots: [],
      crewCertificationsData: [],
      crewMemberBasesData: [],
    };
  }

  // get all unique base airports
  const crewMemberBasesData = Array.from(
    new Set(Data.map((p: Pilot) => p.Profile.BaseAirportCode)),
  ).filter(b => b);

  const certificationsMap = {};
  Data.forEach(pilot => {
    if (pilot.Profile.PilotAircraftCertifications) {
      pilot.Profile.PilotAircraftCertifications.forEach(certification => {
        if (!certificationsMap[certification.AircraftType]) {
          certificationsMap[certification.AircraftType] = [];
        }

        if (
          certification.PilotPosition &&
          certificationsMap[certification.AircraftType].indexOf(certification.PilotPosition) < 0
        ) {
          certificationsMap[certification.AircraftType].push(certification.PilotPosition);
        }
      });
    }
  });
  const crewCertificationsData = Object.keys(certificationsMap)
    .map(key => {
      return {
        value: key,
        label: key,
        children: certificationsMap[key]
          .map(el => ({ value: el, label: el }))
          .sort((a, b) => a.value.localeCompare(b.value)),
      };
    })
    .sort((a, b) => a.value.localeCompare(b.value));

  let pilotList = Data.filter(p => user?.id === p.ID || p.Profile.ShowInCSV2);
  let sortedPilots: Pilot[];

  if (
    localCache.getCached('crewSortOption') === 'custom' &&
    companySettings?.CustomUserDisplayOrder
  ) {
    sortedPilots = sortByCustomUserDisplayOrder(pilotList, user, userRoles, companySettings);
  } else {
    sortedPilots = yield sortByCertifications(pilotList, user);
  }

  let selectedPilotsData = sortedPilots.slice(0, 10);
  const cachedCrewMemberIds = localCache.getCached('selectedCrewMemberIds', []);
  // if any crew members are selected in the local cache, set those in selectedPilots
  if (cachedCrewMemberIds?.length > 0) {
    selectedPilotsData = sortedPilots.filter(p => cachedCrewMemberIds.includes(p?.ID));
  }

  const orderedByFirstNamePilots = orderBy(Data, ['Profile.FirstName'], ['asc']);
  return {
    sortedPilots,
    selectedPilotsData,
    orderedByFirstNamePilots,
    crewCertificationsData,
    crewMemberBasesData,
  };
}

export function* loadPilots() {
  try {
    const {
      sortedPilots,
      selectedPilotsData,
      orderedByFirstNamePilots,
      crewCertificationsData,
      crewMemberBasesData,
    } = yield loadPilotsWorker();

    yield put({
      type: actions.LOAD_PILOTS_SUCCESS,
      payload: sortedPilots,
    });

    yield put({
      type: actions.LOAD_SELECTED_PILOTS_SUCCESS,
      payload: selectedPilotsData,
    });

    yield put({
      type: actions.LOAD_PILOTS_ORDERED_BY_FIRST_NAME,
      payload: orderedByFirstNamePilots,
    });

    const user = yield select(({ user }) => user);
    const companySettings = yield select(({ user: { Company } }) => Company.Settings);
    const localCache = new LocalCache('crewSchedulingV2', user);
    let crewSortOption: string;
    const crewSortCache = localCache.getCached('crewSortOption');
    if (!crewSortCache) {
      if (companySettings.CustomUserDisplayOrder) {
        crewSortOption = 'custom';
        yield call([localCache, 'setCached'], 'crewSortOption', crewSortOption);
        yield call(setCrewSortOptionFromCache);
      } else {
        crewSortOption = 'certTypeAsc';
      }
    } else {
      crewSortOption = crewSortCache;
    }

    yield put({
      type: actions.SET_STATE,
      payload: {
        crewSortOption: crewSortOption,
        crewCertifications: crewCertificationsData,
        crewMemberBases: crewMemberBasesData,
        lastRequestCalledTimestamp: moment(),
        isPilotsLoaded: true,
      },
    });
  } catch (error) {
    console.log(error);
  }
}

export const sortByCustomUserDisplayOrder = (pilotList, authUser, userRoles, companySettings) => {
  const authPilot = pilotList.find(pilot => pilot.ID === authUser.id);
  const onlyPilot = userRoles.includes('pilot') && !userRoles.includes('admin');

  if (
    (userRoles.includes('crewScheduleViewer') ||
      userRoles.includes('taggedScheduleViewer') ||
      companySettings.PilotCanViewAllPilotsCrewSchedule) &&
    onlyPilot
  ) {
    pilotList = pilotList.filter(pilot => pilot.ID !== authUser.id);
  }

  const usersForSorting = pilotList.filter(pilot => pilot.Profile?.DisplayOrder > 0);
  const remainingUsers = pilotList.filter(
    pilot => pilot.Profile?.DisplayOrder === 0 || !pilot.Profile?.DisplayOrder,
  );

  let sortedPilotList = usersForSorting.sort(
    (a, b) => a.Profile.DisplayOrder - b.Profile.DisplayOrder,
  );

  if (
    onlyPilot &&
    (userRoles.includes('crewScheduleViewer') ||
      userRoles.includes('taggedScheduleViewer') ||
      companySettings.PilotCanViewAllPilotsCrewSchedule)
  ) {
    sortedPilotList = [authPilot, ...sortedPilotList].filter(p => p);
  } else if (!companySettings.PilotCanViewAllPilotsCrewSchedule && onlyPilot) {
    sortedPilotList = [authPilot];
  }

  // Concatenate the sorted and remaining users
  sortedPilotList = [...sortedPilotList, ...remainingUsers];

  return sortedPilotList;
};

const sortByCertifications = function*(pilotsArray: Pilot[], authUser) {
  const userRoles = yield select(getUserRole) || [];
  const companySettings = yield select(({ user: { Company } }) => Company.Settings);
  const user = yield select(({ user }) => user);
  // sort pilots by
  // 1. if company.Settings.CustomUserDisplayOrder, sort by this first
  // 2. Pilot Certification asc
  // 3. Aircraft Type asc
  // 4. Date of Hire asc (nulls last)
  // 5. Last Name asc
  // 6. if role is (pilot and not admin && (PilotCanViewAllPilotsCrewSchedule)) show pilot first

  const authPilot = pilotsArray.find(p => p?.ID === authUser?.id);
  const onlyPilot = userRoles?.includes('pilot') && !userRoles?.includes('admin');
  const localCache = new LocalCache('crewSchedulingV2', authUser);

  if (
    (userRoles?.includes('crewScheduleViewer') ||
      userRoles?.includes('taggedScheduleViewer') ||
      companySettings?.PilotCanViewAllPilotsCrewSchedule) &&
    onlyPilot
  ) {
    pilotsArray = pilotsArray.filter(p => p?.ID !== authUser?.id);
  }
  const crewSortCache = localCache.getCached('crewSortOption');
  if (crewSortCache) {
    yield put(setCrewSortOption(crewSortCache));
  }
  let sortedPilots = yield sortCrew(pilotsArray, crewSortCache, user, userRoles, companySettings);
  if (
    (userRoles?.includes('crewScheduleViewer') ||
      userRoles?.includes('taggedScheduleViewer') ||
      companySettings?.PilotCanViewAllPilotsCrewSchedule) &&
    onlyPilot
  ) {
    sortedPilots = [authPilot, ...sortedPilots].filter(p => p);
  } else if (!companySettings?.PilotCanViewAllPilotsCrewSchedule && onlyPilot) {
    sortedPilots = [authPilot];
  }
  return sortedPilots;
};

export const sortCrew = (
  pilots: Pilot[],
  crewSortOption: string,
  authUser: Profile,
  userRoles: string[],
  companySettings: CompanySettings,
) => {
  let sortedPilots: Pilot[];
  if (crewSortOption === 'custom') {
    sortedPilots = sortByCustomUserDisplayOrder(pilots, authUser, userRoles, companySettings);
    return sortedPilots;
  }
  if (crewSortOption === 'certTypeAsc') {
    sortedPilots = pilots.sort(
      firstBy((a: Pilot, b: Pilot) => {
        if (!a.Profile.PilotAircraftCertifications && !b.Profile.PilotAircraftCertifications) {
          return 0;
        }
        if (!a.Profile.PilotAircraftCertifications) {
          return 1;
        }
        if (!b.Profile.PilotAircraftCertifications) {
          return -1;
        }

        return sortCertifications(
          a.Profile.PilotAircraftCertifications,
        )[0].AircraftType.localeCompare(
          sortCertifications(b.Profile.PilotAircraftCertifications)[0].AircraftType,
        );
      })
        .thenBy((a: Pilot, b: Pilot) => {
          if (!a.Profile.PilotAircraftCertifications && !b.Profile.PilotAircraftCertifications) {
            return 0;
          }
          if (!a.Profile.PilotAircraftCertifications) {
            return 1;
          }
          if (!b.Profile.PilotAircraftCertifications) {
            return -1;
          }

          return sortCertifications(
            a.Profile.PilotAircraftCertifications,
          )[0].PilotPosition.localeCompare(
            sortCertifications(b.Profile.PilotAircraftCertifications)[0].PilotPosition,
          );
        })
        .thenBy((a: Pilot, b: Pilot) => {
          if (!a.Profile.DateOfHire && !b.Profile.DateOfHire) {
            return 0;
          }
          if (!a.Profile.DateOfHire) {
            return 1;
          }
          if (!b.Profile.DateOfHire) {
            return -1;
          }
          return moment(a.Profile.DateOfHire).diff(moment(b.Profile.DateOfHire));
        })
        .thenBy((a, b) => a.Profile.LastName.localeCompare(b.Profile.LastName)),
    );
  } else {
    sortedPilots = pilots.sort((a: Pilot, b: Pilot) => {
      if (!a.Profile.FirstName && !b.Profile.FirstName) {
        return 0; // Both names are null or undefined, consider them equal
      } else if (!a.Profile.FirstName) {
        return 1; // Null or undefined names are sorted to the end
      } else if (!b.Profile.FirstName) {
        return -1; // Null or undefined names are sorted to the end
      }
      if (crewSortOption === 'firstNameAlphabeticallyAsc') {
        return a.Profile.FirstName.localeCompare(b.Profile.FirstName);
      } else {
        return a.Profile.LastName.localeCompare(b.Profile.LastName);
      }
    });
  }
  return [...sortedPilots];
};
const sortCertifications = (
  certifications: { AircraftType?: string; PilotPosition?: string }[],
) => {
  return certifications
    .sort((cert1: { AircraftType: string }, cert2: { AircraftType: string }) =>
      cert1?.AircraftType.localeCompare(cert2?.AircraftType),
    )
    .sort((cert1: { PilotPosition: string }, cert2: { PilotPosition: string }) =>
      cert1?.PilotPosition.localeCompare(cert2?.PilotPosition),
    );
};

export function* setCrewSortOptionFromCache() {
  const user = yield select(({ user }) => user);
  const localCache = new LocalCache('crewSchedulingV2', user);
  const crewSortCache = localCache.getCached('crewSortOption');
  if (crewSortCache) {
    yield put(setCrewSortOption(crewSortCache));
  } else {
    yield put(setCrewSortOption('certTypeAsc'));
  }
}
